import React, { useState, useEffect } from 'react';
import { FiArrowRight, FiPlay, FiHeart, FiMessageCircle, FiShare, FiExternalLink } from 'react-icons/fi';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../styles/GalleryPreview.css';
import { supabase } from '../services/supabase';

export default function GalleryPreview() {
  const [data, setData] = useState({
    image_items: [],
    video_items: [],
    social_posts: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedVideos, setExpandedVideos] = useState({});
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchGalleryData();
  }, []);

  const fetchGalleryData = async () => {
    try {
      const { data: galleryData, error } = await supabase
        .from('gallery_preview')
        .select('*')
        .single();

      if (error) {
        console.error('Supabase error:', error);
        setError('Failed to load gallery content');
        return;
      }

      if (galleryData) {
        setData({
          image_items: galleryData.image_items || [],
          video_items: galleryData.video_items || [],
          social_posts: galleryData.social_posts || []
        });
      }
    } catch (error) {
      console.error('Error fetching gallery data:', error);
      setError('Failed to load gallery content');
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeVideoId = (url) => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (!videoId) return '/api/placeholder/400/250';
    
    // Try maxresdefault first, fallback to hqdefault if not available
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const getVideoThumbnail = (video) => {
    // Use custom thumbnail if provided, otherwise use YouTube thumbnail
    if (video.thumbnail && video.thumbnail.trim() !== '') {
      return video.thumbnail;
    }
    return getYouTubeThumbnail(video.youtubeUrl);
  };

  const openYouTubeVideo = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  const toggleVideoDescription = (videoId) => {
    setExpandedVideos(prev => ({
      ...prev,
      [videoId]: !prev[videoId]
    }));
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const openSocialPost = (url) => {
    window.open(url, '_blank');
  };

  if (loading) {
    return <div className="gallery-preview loading">Loading gallery...</div>;
  }

  if (error) {
    return <div className="gallery-preview error">Error: {error}</div>;
  }

  return (
    <section className="gallery-preview">
      <div className="container">
        {/* Header */}
        <div className="preview-header">
          <h2>Notre Galerie</h2>
          <p>Découvrez nos dernières réalisations et projets</p>
        </div>

        

        {/* Images Section */}
        {data.image_items.length > 0 && (
          <div className="section images-section">
            <h3 className="section-title">Nos Réalisations</h3>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{ delay: 5000 }}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 }
              }}
              className="images-swiper"
            >
              {data.image_items.map((item) => (
                <SwiperSlide key={item.id}>
                  <div className="image-slide">
                    <div className="slide-image">
                      <img src={item.src} alt={item.alt} />
                      <div className="slide-overlay">
                        <span className="category-badge">{item.category}</span>
                      </div>
                    </div>
                    <div className="slide-content">
                      <h4>{item.alt}</h4>
                      <p className="slide-description">{item.description}</p>
                      <div className="slide-meta">
                        <span className="location"> {item.location}</span>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* Videos Section */}
        {data.video_items.length > 0 && (
          <div className="section videos-section">
            <h3 className="section-title">Nos Vidéos</h3>
            <div className="videos-grid">
              {data.video_items.map((video) => {
                const isExpanded = expandedVideos[video.id];
                const shouldTruncate = video.description && video.description.length > 100;
                
                return (
                  <div key={video.id} className="video-item">
                    <div className="video-thumbnail" onClick={() => openYouTubeVideo(video)}>
                      <img 
                        src={getVideoThumbnail(video)} 
                        alt={video.title}
                        onError={(e) => {
                          // Fallback to hqdefault if maxresdefault or custom thumbnail fails
                          if (video.thumbnail && video.thumbnail.trim() !== '') {
                            // If custom thumbnail fails, try YouTube thumbnail
                            e.target.src = getYouTubeThumbnail(video.youtubeUrl);
                          } else {
                            // If YouTube maxres fails, try hqdefault
                            const videoId = getYouTubeVideoId(video.youtubeUrl);
                            e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                          }
                        }}
                      />
                      <div className="video-overlay">
                        <FiPlay className="play-icon" />
                      </div>
                    </div>
                    <div className="video-info">
                      <h4 onClick={() => openYouTubeVideo(video)}>{video.title}</h4>
                      <p className="video-description">
                        {isExpanded ? video.description : truncateText(video.description, 100)}
                      </p>
                      {shouldTruncate && (
                        <button 
                          className="voir-plus-btn"
                          onClick={() => toggleVideoDescription(video.id)}
                        >
                          {isExpanded ? 'Voir moins' : 'Voir plus'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Social Media Posts */}
        {data.social_posts.length > 0 && (
          <div className="section social-section">
            <h3 className="section-title">Suivez-nous sur les Réseaux Sociaux</h3>
            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              navigation
              autoplay={{ delay: 4000 }}
              breakpoints={{
                640: { slidesPerView: 2 },
                768: { slidesPerView: 3 },
                1024: { slidesPerView: 4 }
              }}
              className="social-swiper"
            >
              {data.social_posts.map((post) => (
                <SwiperSlide key={post.id}>
                  <div className="social-post" onClick={() => openSocialPost(post.postUrl)}>
                    {post.imageUrl && (
                      <div className="social-post-image">
                        <img src={post.imageUrl} alt={post.caption} />
                      </div>
                    )}
                    <div className="post-header">
                      <span className={`platform-badge ${post.platform.toLowerCase()}`}>
                        {post.platform}
                      </span>
                      <span className="post-time">{post.timeAgo}</span>
                      <FiExternalLink className="external-link" />
                    </div>
                    <div className="post-content">
                      <p>{post.caption}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* Video Modal */}
        {selectedVideo && (
          <div className="video-modal" onClick={closeVideoModal}>
            <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="close-modal" onClick={closeVideoModal}>
                ×
              </button>
              <div className="video-player">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedVideo.youtubeUrl)}?autoplay=1`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="video-modal-info">
                <h3>{selectedVideo.title}</h3>
                <p>{selectedVideo.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
